const HANDLE_SELECTOR =
  '.ant-modal-header, .ant-modal-title, .ant-modal-confirm-title';
const IGNORE_SELECTOR =
  '.ant-modal-close, .ant-modal-footer, button, a, input, textarea, select, .ant-picker, .ant-select, .ant-switch, .ant-checkbox, .ant-radio';

export type ModalTranslate = { x: number; y: number };

export function parseCssTranslate(transform: string): ModalTranslate {
  const text = String(transform || '').trim();
  const match3d = text.match(
    /translate3d\(\s*(-?\d+(?:\.\d+)?)px\s*,\s*(-?\d+(?:\.\d+)?)px/i,
  );
  if (match3d) return { x: Number(match3d[1]), y: Number(match3d[2]) };
  const match = text.match(
    /translate\(\s*(-?\d+(?:\.\d+)?)px\s*,\s*(-?\d+(?:\.\d+)?)px/i,
  );
  if (match) return { x: Number(match[1]), y: Number(match[2]) };
  return { x: 0, y: 0 };
}

export function resolveDraggableModal(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof Element)) return null;
  if (target.closest(IGNORE_SELECTOR)) return null;
  const handle = target.closest(HANDLE_SELECTOR);
  if (!handle) return null;
  const modal = handle.closest('.ant-modal');
  return modal instanceof HTMLElement ? modal : null;
}

export function nextModalTranslate(input: {
  startX: number;
  startY: number;
  originClientX: number;
  originClientY: number;
  clientX: number;
  clientY: number;
  rect: { left: number; top: number; width: number; height: number };
  viewport: { width: number; height: number };
}): ModalTranslate {
  const dx = input.clientX - input.originClientX;
  const dy = input.clientY - input.originClientY;
  const minVisible = 56;
  let visualLeft = input.rect.left + dx;
  let visualTop = input.rect.top + dy;
  visualLeft = Math.min(
    input.viewport.width - minVisible,
    Math.max(minVisible - input.rect.width, visualLeft),
  );
  visualTop = Math.min(
    input.viewport.height - minVisible,
    Math.max(0, visualTop),
  );
  return {
    x: input.startX + (visualLeft - input.rect.left),
    y: input.startY + (visualTop - input.rect.top),
  };
}

export function isHiddenAntModalWrap(wrap: HTMLElement): boolean {
  if (wrap.getAttribute('aria-hidden') === 'true') return true;
  if (wrap.classList.contains('ant-modal-wrap-hidden')) return true;
  if (wrap.hidden || wrap.style.display === 'none') return true;
  const root = wrap.closest('.ant-modal-root');
  if (root instanceof HTMLElement) {
    if (root.classList.contains('ant-modal-hidden')) return true;
    if (root.getAttribute('aria-hidden') === 'true') return true;
  }
  return false;
}

/** antd 6 在 onOk Promise 期间给 wrap 设 pointer-events:none；关闭后若未清掉会挡住整页。 */
export function releaseHiddenModalWrapLocks(root: ParentNode = document): void {
  root.querySelectorAll('.ant-modal-wrap').forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    if (!isHiddenAntModalWrap(node)) return;
    if (node.style.pointerEvents === 'none') {
      node.style.removeProperty('pointer-events');
    }
  });
}

export function clearDraggableModalDrag(doc: Document = document, modal?: HTMLElement | null): void {
  modal?.classList.remove('lce-modal-dragging');
  doc.body.classList.remove('lce-modal-dragging');
}

/** 拖标题栏移动 Ant Design Modal；Modal 挂在 body 上，需监听 document。 */
export function installDraggableAntModals(doc: Document = document): () => void {
  let draggingModal: HTMLElement | null = null;
  let captureTarget: Element | null = null;
  let activePointerId: number | null = null;

  const endDrag = () => {
    if (captureTarget && activePointerId != null) {
      try {
        captureTarget.releasePointerCapture?.(activePointerId);
      } catch {
        /* ignore */
      }
    }
    clearDraggableModalDrag(doc, draggingModal);
    draggingModal = null;
    captureTarget = null;
    activePointerId = null;
    releaseHiddenModalWrapLocks(doc);
  };

  const onPointerDown = (event: PointerEvent) => {
    if (event.button !== 0) return;
    const modal = resolveDraggableModal(event.target);
    if (!modal) return;

    const start = parseCssTranslate(modal.style.transform);
    const originClientX = event.clientX;
    const originClientY = event.clientY;
    const rect = modal.getBoundingClientRect();
    const viewport = {
      width: doc.documentElement.clientWidth || window.innerWidth,
      height: doc.documentElement.clientHeight || window.innerHeight,
    };

    draggingModal = modal;
    modal.classList.add('lce-modal-dragging');
    doc.body.classList.add('lce-modal-dragging');
    modal.style.transition = 'none';
    const handle = event.target instanceof Element ? event.target : modal;
    captureTarget = handle;
    activePointerId = event.pointerId;
    try {
      handle.setPointerCapture?.(event.pointerId);
    } catch {
      /* ignore */
    }

    const onMove = (moveEvent: PointerEvent) => {
      const next = nextModalTranslate({
        startX: start.x,
        startY: start.y,
        originClientX,
        originClientY,
        clientX: moveEvent.clientX,
        clientY: moveEvent.clientY,
        rect,
        viewport,
      });
      modal.style.transform = `translate3d(${next.x}px, ${next.y}px, 0)`;
    };
    const onUp = () => {
      doc.removeEventListener('pointermove', onMove);
      doc.removeEventListener('pointerup', onUp);
      doc.removeEventListener('pointercancel', onUp);
      endDrag();
    };

    doc.addEventListener('pointermove', onMove);
    doc.addEventListener('pointerup', onUp);
    doc.addEventListener('pointercancel', onUp);
  };

  const observer = new MutationObserver(() => {
    if (draggingModal && !draggingModal.isConnected) endDrag();
    releaseHiddenModalWrapLocks(doc);
  });
  observer.observe(doc.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-hidden', 'class', 'style'],
  });

  doc.addEventListener('pointerdown', onPointerDown, true);
  doc.body.classList.add('lce-draggable-modals');
  return () => {
    observer.disconnect();
    doc.removeEventListener('pointerdown', onPointerDown, true);
    doc.body.classList.remove('lce-draggable-modals');
    endDrag();
  };
}
